import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHeader, Loader } from "@/components/common/Loader";
import { useFetch } from "@/lib/api";
import { TICKET_STATUS, STATUS_COLORS, fmtDate, OPEN_STATUSES, greeting } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const Kpi = ({ label, value, hint, to, testid }) => {
  const body = (
    <>
      <p className="eyebrow">{label}</p>
      <p data-testid={testid} className="font-serif text-5xl font-light mt-4">{value}</p>
      {hint && <p className="text-xs text-warm-text mt-2">{hint}</p>}
    </>
  );
  return to ? <Link to={to} className="lux-card-link p-6 block">{body}</Link> : <div className="lux-card p-6">{body}</div>;
};

function Bars({ items, colorFor }) {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div className="space-y-4">
      {items.map((i) => (
        <div key={i.name}>
          <div className="flex justify-between text-sm"><span>{i.name}</span><span className="text-warm-text">{i.count}</span></div>
          <div className="h-1 bg-ivory-deep mt-2"><div className="h-1 transition-all" style={{ width: `${(i.count / max) * 100}%`, background: colorFor ? colorFor(i) : "#D4AF37" }} /></div>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data, loading } = useFetch("/admin/dashboard");
  if (loading || !data) return <Loader />;
  const s = data.tickets_by_status;
  const openCount = OPEN_STATUSES.reduce((a, k) => a + (s[k] || 0), 0);
  const statusItems = Object.keys(TICKET_STATUS).map((k) => ({ key: k, name: TICKET_STATUS[k], count: s[k] || 0 }));
  return (
    <div>
      <PageHeader eyebrow="Cruscotto" title={`${greeting()}, ${user.name.split(" ")[0]}`} subtitle="Uno sguardo d'insieme su iscrizioni e assistenza." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Iscrizioni in attesa" value={data.pending_count} to="/admin/centri?stato=pending" testid="kpi-pending" />
        <Kpi label="Ticket aperti" value={openCount} to="/admin/ticket" testid="kpi-open-tickets" />
        <Kpi label="Prima risposta" value={data.avg_first_response_hours == null ? "—" : `${data.avg_first_response_hours} h`} hint="Tempo medio" testid="kpi-first-response" />
        <Kpi label="Centri attivi" value={data.active_centers} to="/admin/centri?stato=approved" testid="kpi-active-centers" />
      </div>
      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <section className="lux-card p-6 sm:p-8" data-testid="dashboard-tickets-by-status">
          <h2 className="text-2xl font-normal mb-6">Ticket per stato</h2>
          <Bars items={statusItems} colorFor={(i) => STATUS_COLORS[i.key]} />
        </section>
        <section className="lux-card p-6 sm:p-8" data-testid="dashboard-top-categories">
          <h2 className="text-2xl font-normal mb-6">Categorie più frequenti</h2>
          {data.top_categories.length ? <Bars items={data.top_categories} /> : <p className="text-sm text-warm-text">Ancora nessun ticket.</p>}
        </section>
        <section className="lux-card p-6 sm:p-8" data-testid="dashboard-pending-list">
          <h2 className="text-2xl font-normal mb-6">Da approvare</h2>
          {data.pending.length === 0 ? <p className="text-sm text-warm-text">Nessuna richiesta in attesa.</p> : (
            <ul className="divide-y divide-warm/30">
              {data.pending.map((c) => (
                <li key={c.id}>
                  <Link to={`/admin/centri/${c.id}`} data-testid={`pending-center-${c.id}`} className="flex items-center justify-between py-3 group">
                    <span><span className="block text-sm">{c.center_name}</span><span className="text-xs text-warm-text">{c.city} · {fmtDate(c.created_at)}</span></span>
                    <ArrowUpRight className="w-4 h-4 text-warm group-hover:text-gold" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
